import type { CSSProperties } from 'react';

import { C } from '../../../../../lib/design';
import { Ic, ICON_PATHS } from '../../../../../lib/ui';
import { useI18n } from '../../../../../i18n';
import type { Documento } from '../../../../../types';
import { panelStyles } from './document-panel-styles';
import { AprobadoresPreview } from '../../../../platform/sign/AprobadoresPreview';
import { useSignMode } from '../../../../platform/sign/useSignMode';
import { DocumentoWorkflowBar } from './DocumentoWorkflowBar';
import { ArtefactoToolbarActions } from '../../../../shared/components/ArtefactoToolbarActions';

type PanelTab = 'contenido' | 'versiones' | 'referencias' | 'archivos';

// 0661.1 — header unificado cross-artefacto. Eliminados:
//   - <StatusBadge> (lo gestiona el WorkflowBar interno del wrapper)
//   - <select> versions dropdown (lo provee el historial popover del wrapper)
//   - <span>{editionLabel}</span> (lo provee la pill `Ed. N` del wrapper)
//   - botones <PDF>/<Expand> independientes (los provee <ArtefactoToolbarActions>)
// Resultado: misma UI/UX que OrganigramaPage (1 línea con wrapper + toolbar shared).

interface DocumentPanelHeaderProps {
  doc: Documento;
  versionsCount: number;
  activeTab: PanelTab;
  setActiveTab: (tab: PanelTab) => void;
  zoom: number;
  zoomIn: () => void;
  zoomOut: () => void;
  isEditing: boolean;
  saving: boolean;
  exportingWord: boolean;
  lastSaved: Date | null;
  canEdit: boolean;
  readOnly: boolean;
  viewerMode: boolean;
  showVIA: boolean;
  refCount: number;
  onEnterEditMode: () => void;
  onExitEditMode: () => Promise<void>;
  onManualSave: () => Promise<void>;
  onDiscardOpen: () => void;
  onToggleVIA: () => void;
  onExportWord: () => Promise<void>;
  onExportPdf: () => Promise<void>;
  onExpandFullscreen?: () => void;
  isFullscreen?: boolean;
  /** Callback opcional cuando el usuario selecciona una versión histórica vía historial popover. */
  onSelectVersion?: (versionId: string, numeroEdicion: number) => void;
  /**
   * F2.1+F2.2 — datos para el preview de aprobadores. Si se pasan y el
   * estado es APROBADO + Sign activo, se muestra el destinatario antes
   * del botón "Enviar a aprobación". El estado lo emite el wrapper via
   * onVersionStateChange (vía DocumentPanel).
   */
  aprobadoresEntidadTipo?: 'version_documento' | 'flujograma';
  aprobadoresEntidadId?: string;
  /** Estado actual de la versión activa (emitido por el wrapper). Usado para gating del preview. */
  versionActivaEstado?: string | null;
}

export function DocumentPanelHeader({
  doc,
  versionsCount,
  activeTab,
  setActiveTab,
  zoom,
  zoomIn,
  zoomOut,
  isEditing,
  saving,
  exportingWord,
  lastSaved,
  canEdit,
  readOnly,
  viewerMode,
  showVIA,
  refCount,
  onEnterEditMode,
  onExitEditMode,
  onManualSave,
  onDiscardOpen,
  onToggleVIA,
  onExportWord,
  onExportPdf,
  onExpandFullscreen,
  isFullscreen,
  onSelectVersion,
  aprobadoresEntidadTipo,
  aprobadoresEntidadId,
  versionActivaEstado,
}: DocumentPanelHeaderProps) {
  const { t } = useI18n();
  const { signActive } = useSignMode();
  const mostrarAprobadoresPreview =
    signActive &&
    versionActivaEstado === 'APROBADO' &&
    !!aprobadoresEntidadTipo &&
    !!aprobadoresEntidadId;

  return (
    <div style={panelStyles.unifiedHeader}>
      {/* Doc code + name */}
      <span style={panelStyles.docCode}>{doc.codigo}</span>
      <span style={headerLocal.docName}>{doc.nombre}</span>

      {/* Separator */}
      <div style={panelStyles.headerSep} />

      {/* Tab combobox */}
      <select
        value={activeTab}
        onChange={(e) => setActiveTab(e.target.value as PanelTab)}
        style={headerLocal.tabSelect}
      >
        <option value="contenido">{t.viewer.tabs.content}</option>
        {!viewerMode && (
          <option value="versiones">{`${t.viewer.tabs.versions} (${versionsCount})`}</option>
        )}
        <option value="referencias">{`${t.viewer.tabs.references} (${refCount})`}</option>
        <option value="archivos">{t.archivos.tabArchivos}</option>
      </select>

      {/* Separator */}
      <div style={panelStyles.headerSep} />

      {/* Zoom controls (only show on contenido tab) */}
      {activeTab === 'contenido' && (
        <>
          <button onClick={zoomOut} style={panelStyles.zoomBtn}>
            {'−'}
          </button>
          <span style={panelStyles.zoomLabel}>{zoom}%</span>
          <button onClick={zoomIn} style={panelStyles.zoomBtn}>
            +
          </button>
        </>
      )}

      {/* Spacer */}
      <div style={{ flex: 1 }} />

      {/* Auto-save indicator (inline durante edit) */}
      {isEditing && lastSaved && (
        <span style={{ fontSize: 10, color: C.textMut, whiteSpace: 'nowrap' as const }}>
          {t.common.autoSave} {lastSaved.toLocaleTimeString('es-ES')}
        </span>
      )}

      {/* Admin actions — solo en panel mode (no readOnly, no viewerMode) */}
      {!readOnly && !viewerMode && (
        <>
          {isEditing ? (
            <>
              <button
                className="dpanel-btn-primary"
                style={panelStyles.btnPrimary}
                onClick={onManualSave}
                disabled={saving}
              >
                <Ic d={ICON_PATHS.save} s={12} c="#fff" />
                {saving ? t.common.guardando : t.common.guardar}
              </button>
              <button className="dpanel-btn" style={panelStyles.btn} onClick={onDiscardOpen}>
                {t.common.discard}
              </button>
              <button className="dpanel-btn" style={panelStyles.btn} onClick={onExitEditMode}>
                {t.common.closeEditor}
              </button>
            </>
          ) : (
            <>
              {canEdit && (
                <button
                  className="dpanel-btn"
                  style={panelStyles.btn}
                  onClick={onEnterEditMode}
                  title={t.common.editar}
                >
                  <Ic d={ICON_PATHS.edit} s={12} c={C.textSec} />
                  <span style={{ fontSize: 11 }}>{t.common.editar}</span>
                </button>
              )}
              {/* ADR-025 ext 0661: barra workflow shared cross-artefacto.
                  Provee: pill Ed.N + badge etapa + botones acción legales +
                  Historial popover + Nueva edición. Misma UX que organigrama. */}
              <div style={panelStyles.headerSep} />
              {mostrarAprobadoresPreview && (
                <AprobadoresPreview
                  entidadTipo={aprobadoresEntidadTipo as NonNullable<typeof aprobadoresEntidadTipo>}
                  entidadId={aprobadoresEntidadId as NonNullable<typeof aprobadoresEntidadId>}
                />
              )}
              <DocumentoWorkflowBar codigo={doc.codigo} enabled onSelectVersion={onSelectVersion} />
            </>
          )}
        </>
      )}

      {/* V·IA button — alineado 32×32 con ArtefactoToolbarActions (0661.3) */}
      {(!readOnly || viewerMode) && (
        <button
          className="dpanel-btn"
          style={{ ...headerLocal.iconBtn, color: showVIA ? '#33BCE1' : C.azul1 }}
          onClick={onToggleVIA}
          title={t.common.viaAsistente}
          aria-label={t.common.viaAsistente}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* Estrella 4 puntas — V·IA */}
            <path d="M12 2 L13.5 9.5 L21 11 L13.5 12.5 L12 20 L10.5 12.5 L3 11 L10.5 9.5 Z" />
          </svg>
        </button>
      )}

      {/* Word export — alineado 32×32 con ArtefactoToolbarActions (0661.3) */}
      {!readOnly && !viewerMode && (
        <button
          className="dpanel-btn"
          style={headerLocal.iconBtn}
          onClick={onExportWord}
          disabled={exportingWord}
          title={exportingWord ? t.viewerHeader.exportando : t.viewerHeader.exportarDocx}
          aria-label={t.viewerHeader.exportarDocx}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            {/* Marco doc con esquina doblada — patrón Adobe DOC */}
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <text
              x="12"
              y="17.5"
              fontFamily="DM Sans, sans-serif"
              fontSize="5"
              fontWeight="800"
              textAnchor="middle"
              fill="currentColor"
              stroke="none"
              letterSpacing="0.4"
            >
              DOC
            </text>
          </svg>
        </button>
      )}

      {/* PDF + Fullscreen — toolbar canónico shared cross-artefacto. */}
      <ArtefactoToolbarActions
        onExportPdf={onExportPdf}
        onFullscreen={onExpandFullscreen ?? null}
        isFullscreen={isFullscreen}
        compact
      />
    </div>
  );
}

const headerLocal: Record<string, CSSProperties> = {
  docName: {
    fontSize: 12,
    fontWeight: 500,
    color: '#1A1D23',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    minWidth: 0,
    maxWidth: 200,
  },
  tabSelect: {
    padding: '2px 24px 2px 8px',
    borderRadius: 4,
    border: '1px solid #d5d5d5',
    fontSize: 12,
    fontWeight: 500,
    color: '#0E243E',
    backgroundColor: '#fff',
    cursor: 'pointer',
    fontFamily: 'inherit',
    appearance: 'none' as const,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23666' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 6px center',
    flexShrink: 0,
  },
  // 0661.3 — botones icon-only 32×32 alineados con ArtefactoToolbarActions
  // (PDF + Fullscreen). Garantiza paridad visual de altura/border/radius/color
  // con el resto de la toolbar shared. Usado por V·IA y W (Word export).
  iconBtn: {
    display: 'inline-flex',
    flexDirection: 'row' as const,
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    background: '#fff',
    color: '#1F3864',
    border: '1px solid #D1D5DB',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 11,
    fontWeight: 600,
    transition: 'background 120ms ease, border-color 120ms ease, color 120ms ease',
    flexShrink: 0,
  },
};
